import React, { useState, useCallback, useEffect } from "react";
import instance, { apiEncryptRequest, decryptData } from "../../Api";
import { useAuth } from "../../AuthContext";
import toast, { Toaster } from "react-hot-toast";
import AppToast from "../../AppToast";
import Loader from "../common/Loader";

/**
 * TwoFactorSetup component
 * 
 * Usage in Profile.jsx — replace the existing "2FA ON" stat card:
 * 
 *   import TwoFactorSetup from "./TwoFactorSetup";
 * 
 *   // Inside render, after your stat cards:
 *   <TwoFactorSetup isMobile={isMobile} userData={userData} onStatusChange={GetMyDetails} />
 */

const TwoFactorSetup = ({ isMobile, userData, onStatusChange }) => {
  const { smuser } = useAuth();

  const [loading,      setLoading]      = useState(false);
  const [step,         setStep]         = useState("idle");   // idle | setup | confirm
  const [qrImage,      setQrImage]      = useState("");
  const [secret,       setSecret]       = useState("");
  const [code,         setCode]         = useState("");
  const [password,     setPassword]     = useState("");
  const [showDisable,  setShowDisable]  = useState(false);

  const isEnabled = userData?.totp_enabled === 1;

  const showToast = useCallback((msg) =>
    toast((t) => <AppToast id={t.id} message={msg} isMobile={isMobile} />), [isMobile]);

  // ── Start setup: get QR from backend ────────────────────────────────────────
  const handleSetup = useCallback(async () => {
    setLoading(true);
    try {
      const enc  = await apiEncryptRequest({ user_id: smuser.id });
      const res  = await instance.post("/2fa/setup", enc);
      const data = await decryptData(res.data);

      if (data.success) {
        setQrImage(data.qr_image);
        setSecret(data.secret);
        setStep("setup");
      } else {
        showToast(data.message || "Setup failed");
      }
    } catch {
      showToast("Something went wrong. Please try again.");
    }
    setLoading(false);
  }, [smuser?.id, showToast]);

  // ── Enable: verify first code ────────────────────────────────────────────────
  const handleEnable = useCallback(async () => {
    if (!code || code.length !== 6) { showToast("Enter 6-digit code"); return; }
    setLoading(true);
    try {
      const enc  = await apiEncryptRequest({ user_id: smuser.id, code });
      const res  = await instance.post("/2fa/enable", enc);
      const data = await decryptData(res.data);

      if (data.success) {
        showToast("✅ 2FA enabled! Your account is now protected.");
        setStep("idle");
        setCode("");
        if (onStatusChange) onStatusChange();
      } else {
        showToast(data.message || "Invalid code.");
      }
    } catch {
      showToast("Something went wrong.");
    }
    setLoading(false);
  }, [smuser?.id, code, showToast, onStatusChange]);

  // ── Disable: requires password ───────────────────────────────────────────────
  const handleDisable = useCallback(async () => {
    if (!password) { showToast("Enter your password"); return; }
    setLoading(true);
    try {
      const enc  = await apiEncryptRequest({ user_id: smuser.id, password });
      const res  = await instance.post("/2fa/disable", enc);
      const data = await decryptData(res.data);

      if (data.success) {
        showToast("2FA has been disabled.");
        setShowDisable(false);
        setPassword("");
        if (onStatusChange) onStatusChange();
      } else {
        showToast(data.message || "Failed to disable 2FA.");
      }
    } catch {
      showToast("Something went wrong.");
    }
    setLoading(false);
  }, [smuser?.id, password, showToast, onStatusChange]);

  // ── Auto-submit when 6 digits entered ────────────────────────────────────────
  const handleCodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(val);
    if (val.length === 6) {
      setTimeout(() => handleEnable(), 100);
    }
  };

  // ── Styles ────────────────────────────────────────────────────────────────────
  const cardStyle = {
    background: "rgba(7,82,139,0.6)",
    border: "2px solid rgba(255,255,255,0.2)",
    borderRadius: 14,
    padding: "20px",
    marginTop: 20,
    fontFamily: "inherit",
  };

  const labelStyle = {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: "rgba(255,255,255,0.5)",
    marginBottom: 8,
    display: "block",
  };

  const inputStyle = {
    width: "100%",
    background: "rgba(255,255,255,0.08)",
    border: "2px solid rgba(255,255,255,0.2)",
    borderRadius: 10,
    padding: "10px 14px",
    color: "white",
    fontSize: 16,
    outline: "none",
    boxSizing: "border-box",
  };

  const codeInputStyle = {
    ...inputStyle,
    fontSize: 28,
    fontWeight: 800,
    textAlign: "center",
    letterSpacing: "0.4em",
    marginBottom: 14,
  };

  const btnPrimary = {
    background: "#e97730",
    color: "white",
    border: "none",
    borderRadius: 25,
    padding: "11px 28px",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    width: "100%",
    marginBottom: 10,
  };

  const btnSecondary = {
    background: "transparent",
    color: "rgba(255,255,255,0.6)",
    border: "1px solid rgba(255,255,255,0.3)",
    borderRadius: 25,
    padding: "9px 22px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    width: "100%",
  };

  const btnDanger = {
    background: "rgba(240,98,146,0.15)",
    color: "#F06292",
    border: "1px solid rgba(240,98,146,0.3)",
    borderRadius: 25,
    padding: "9px 22px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    width: "100%",
    marginBottom: 10,
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={cardStyle}>
      <Toaster position={isMobile ? "top-center" : "bottom-right"} reverseOrder={false} />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ color: "white", fontSize: 16, fontWeight: 800 }}>
          🔐 Two-Factor Authentication
        </div>
        <span style={{
          fontSize: 10, fontWeight: 800, textTransform: "uppercase",
          padding: "3px 10px", borderRadius: 5, letterSpacing: "0.08em",
          background: isEnabled ? "rgba(52,199,138,0.15)" : "rgba(240,98,146,0.12)",
          color: isEnabled ? "#34C78A" : "#F06292",
          border: `1px solid ${isEnabled ? "rgba(52,199,138,0.25)" : "rgba(240,98,146,0.2)"}`,
        }}>
          {isEnabled ? "● Active" : "● Disabled"}
        </span>
      </div>

      <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 13, lineHeight: 1.6, marginBottom: 18 }}>
        {isEnabled
          ? "Your account is protected with Google Authenticator."
          : "Protect your account with Google Authenticator. Each login will require a 6-digit code."}
      </p>

      {/* ── IDLE: show enable/disable buttons ── */}
      {step === "idle" && (
        <>
          {!isEnabled && (
            <button style={btnPrimary} onClick={handleSetup} disabled={loading}>
              {loading ? "Loading…" : "Enable Google Authenticator"}
            </button>
          )}

          {isEnabled && !showDisable && (
            <button style={btnDanger} onClick={() => setShowDisable(true)}>
              Disable 2FA
            </button>
          )}

          {isEnabled && showDisable && (
            <div>
              <span style={labelStyle}>Enter password to disable 2FA</span>
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <input
                  type="password"
                  style={{ ...inputStyle, flex: 1 }}
                  placeholder="Current password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  style={{ ...btnDanger, width: "auto", padding: "10px 18px", margin: 0 }}
                  onClick={handleDisable}
                  disabled={loading}
                >
                  Disable
                </button>
              </div>
              <button style={btnSecondary} onClick={() => { setShowDisable(false); setPassword(""); }}>
                Cancel
              </button>
            </div>
          )}
        </>
      )}

      {/* ── SETUP: show QR + manual secret ── */}
      {step === "setup" && (
        <div>
          {/* Step 1 */}
          <div style={{ marginBottom: 16 }}>
            <span style={labelStyle}>Step 1 — Download authenticator app</span>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 13 }}>
              Install <strong style={{ color: "white" }}>Google Authenticator</strong> or{" "}
              <strong style={{ color: "white" }}>Authy</strong> on your phone.
            </p>
          </div>

          {/* Step 2: QR */}
          <div style={{ marginBottom: 16 }}>
            <span style={labelStyle}>Step 2 — Scan QR code</span>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
              {qrImage ? (
                <img src={qrImage} alt="QR Code"
                  style={{ width: 160, height: 160, background: "white", padding: 6, borderRadius: 8 }} />
              ) : (
                <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 13 }}>
                  QR unavailable — use manual key below
                </div>
              )}
            </div>

            {/* Manual secret */}
            <span style={{ color: "rgba(255,255,255,0.55)", fontSize: 12, display: "block", marginBottom: 6 }}>
              Or enter manually:
            </span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{
                flex: 1, background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.25)",
                borderRadius: 8, padding: "8px 12px",
                fontFamily: "monospace", fontSize: 15, fontWeight: 700,
                color: "#E8C870", letterSpacing: "0.12em", wordBreak: "break-all",
              }}>
                {secret}
              </div>
              <button
                style={{ background: "rgba(201,168,76,0.12)", border: "1px solid rgba(201,168,76,0.3)",
                  borderRadius: 7, padding: "8px 12px", color: "#C9A84C",
                  fontWeight: 800, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}
                onClick={() => navigator.clipboard.writeText(secret).then(() => showToast("Copied!"))}
              >
                Copy
              </button>
            </div>
          </div>

          {/* Step 3: Code input */}
          <div style={{ marginBottom: 16 }}>
            <span style={labelStyle}>Step 3 — Enter the 6-digit code</span>
            <input
              type="text"
              inputMode="numeric"
              style={codeInputStyle}
              placeholder="000000"
              value={code}
              onChange={handleCodeChange}
              maxLength={6}
              autoFocus
            />
            <button style={btnPrimary} onClick={handleEnable} disabled={code.length !== 6 || loading}>
              {loading ? "Activating…" : "Activate 2FA"}
            </button>
            <button style={btnSecondary} onClick={() => { setStep("idle"); setCode(""); setQrImage(""); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>Loading…</span>
        </div>
      )}
    </div>
  );
};

export default TwoFactorSetup;